import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  EMPTY_LIST,
  EMPTY_OBJECT,
  EMPTY_STRING,
} from '../../../constants/common'
import { useUserData } from '../../../hooks'
import Button from '../../Button'
import { ConfirmationModal } from '../../Confirmation'
import Modal, { Box, Buttons, Content, Header } from '../../Modal'
import {
  getEmailExternalVariables,
  getEmailGoogleTemplates,
  refreshEmailGoogleTemplates,
} from '../Email.api'
import { getEmailFormTemplateDependeciesApiParams } from '../EmailCreationForm'
import { EmailGoogleTemplateEmpty } from '../EmailGoogleTemplateEmpty'
import { EmailGoogleTemplatesList } from '../EmailGoogleTemplatesList'
import { EmailGoogleTemplateWrapper } from '../EmailGoogleTemplateWrapper'

import * as styles from './EmailGoogleTemplateModal.module.css'

function EmailGoogleTemplateModal({
  docTemplate = null,
  folder,
  templateEntityApiParams,
  onAddTemplate,
  onClose,
}) {
  const { t } = useTranslation()

  const user = useUserData()

  const [templates, setTemplates] = useState(EMPTY_LIST)
  const [variable, setVariable] = useState(null)
  const [selectedTemplate, setSelectedTemplate] = useState(docTemplate)
  const [externalVariables, setExternalVariables] = useState(
    docTemplate?.external_variables || EMPTY_OBJECT
  )
  const [isCloseConfirm, setCloseConfirm] = useState(false)
  const [isAdding, setAdding] = useState(false)
  const [search, setSearch] = useState(EMPTY_STRING)

  function handleClickTemplate(template) {
    setSelectedTemplate(template)
    setExternalVariables({})

    const external_variables = {}

    getEmailExternalVariables(template?.id).then(({ data }) => {
      data.forEach(field => {
        external_variables[field] = EMPTY_STRING
      })

      setExternalVariables(external_variables)
    })

    setVariable(null)
  }

  function handleHighLightVariable(variable) {
    setVariable(`{{${variable}}}`)
  }

  async function handleAddTemplate() {
    setAdding(true)

    try {
      const hasMissingVariables = await onAddTemplate({
        ...selectedTemplate,
        dependencies: getEmailFormTemplateDependeciesApiParams({
          templateEntityApiParams,
          user,
        }),
        external_variables: externalVariables,
      })

      if (!hasMissingVariables) {
        onClose()
      }
    } finally {
      setAdding(false)
    }
  }

  function initTemplates() {
    getEmailGoogleTemplates(search).then(({ data }) => {
      setTemplates(data[folder] || EMPTY_LIST)
    })
  }

  useEffect(() => {
    initTemplates()
  }, [folder, search])

  function handleChangeExternalVariables(e) {
    const { name, value } = e.target

    setExternalVariables({ ...externalVariables, [name]: value })
  }

  function handleRefreshTemplates() {
    refreshEmailGoogleTemplates().then(() => initTemplates())
  }

  function handleClose() {
    if (!isAdding) {
      if (selectedTemplate) {
        setCloseConfirm(true)
      } else {
        onClose()
      }
    }
  }

  function handleCancelCloseConfirm() {
    setCloseConfirm(false)
  }

  if (isCloseConfirm) {
    return (
      <ConfirmationModal
        actionButtonsProps={{
          cancelText: t('Modal.CommonGoBackBtnKey'),
          submitText: t('Modal.CommonDiscardBtnKey'),
        }}
        description={t('Modal.CancelConfirmationTitleKey')}
        onCancel={handleCancelCloseConfirm}
        onSubmit={onClose}
      />
    )
  }

  return (
    <Modal onClose={handleClose}>
      <Box isFullScreen>
        <Header
          text={t('Email.EmailGoogleTemplateTitleKey')}
          onClose={handleClose}
        />
        <Content
          isFullScreen
          containerClassName={styles.contentContainer}
          contentWrapperClassName={styles.contentWrapper}
        >
          <EmailGoogleTemplatesList
            search={search}
            selectedTemplate={selectedTemplate}
            templates={templates}
            onChangeSearch={setSearch}
            onRefreshTemplates={handleRefreshTemplates}
            onSelectTemplate={handleClickTemplate}
          />
          {selectedTemplate ? (
            <EmailGoogleTemplateWrapper
              activeVariable={variable}
              template={selectedTemplate}
              variables={externalVariables}
              onChangeVariables={handleChangeExternalVariables}
              onHighlightVariable={handleHighLightVariable}
            />
          ) : (
            <EmailGoogleTemplateEmpty />
          )}
        </Content>
        <Buttons size='l'>
          <Button.Contained
            disabled={!selectedTemplate}
            isLoading={isAdding}
            text={t('Modal.AddBtnKey')}
            onClick={handleAddTemplate}
          />
          <Button.Outlined
            disabled={isAdding}
            text={t('Modal.CancelBtnKey')}
            onClick={handleClose}
          />
        </Buttons>
      </Box>
    </Modal>
  )
}

export default EmailGoogleTemplateModal
